import { lazy, Suspense } from 'react';

import { Loading } from '../../components/ui.jsx';

/**
 * Provisioning in the web app: the client's services, and for staff services, servers, server groups
 * and the failed-actions queue. Pages load lazily (one chunk per page group).
 */
const page = (load, name) => () => load().then((m) => ({ Component: m[name] }));
const clientServices = () => import('./ClientServices.jsx');
const clientDetail = () => import('./ClientServiceDetail.jsx');
const adminServices = () => import('./AdminServices.jsx');
const adminDetail = () => import('./AdminServiceDetail.jsx');
const servers = () => import('./Servers.jsx');
const groups = () => import('./ServerGroups.jsx');

/** A lazily loaded component with its own Suspense boundary (dashboards and tabs don't have one). */
const deferred = (load, name) => {
  const Lazy = lazy(() => load().then((m) => ({ default: m[name] })));
  return function Deferred(props) {
    return (
      <Suspense fallback={<Loading />}>
        <Lazy {...props} />
      </Suspense>
    );
  };
};

const ActiveServicesWidget = deferred(clientServices, 'ActiveServicesWidget');
const FailedActionsWidget = deferred(adminServices, 'FailedActionsWidget');
const ClientServicesTab = deferred(adminServices, 'ClientServicesTab');

export default {
  name: 'provisioning',
  clientRoutes: [
    { path: 'services', lazy: page(clientServices, 'ClientServices') },
    { path: 'services/:uuid', lazy: page(clientDetail, 'ClientServiceDetail') },
  ],
  adminRoutes: [
    { path: 'services', lazy: page(adminServices, 'AdminServices') },
    { path: 'services/:uuid', lazy: page(adminDetail, 'AdminServiceDetail') },
    { path: 'servers', lazy: page(servers, 'Servers') },
    { path: 'servers/groups', lazy: page(groups, 'ServerGroups') },
    { path: 'servers/:id', lazy: page(servers, 'ServerDetail') },
    { path: 'provisioning/failed', lazy: page(groups, 'FailedActions') },
  ],
  clientNav: [{ key: 'services', to: '/services', label: 'Services', order: 20, auth: true }],
  adminNav: [
    { section: 'services', to: '/admin/services', label: 'Services', permission: 'admin.services.read', order: 10 },
    { section: 'services', to: '/admin/servers', label: 'Servers', permission: 'admin.servers.read', order: 20 },
    {
      section: 'services',
      to: '/admin/provisioning/failed',
      label: 'Failed actions',
      permission: 'admin.services.read',
      order: 30,
    },
  ],
  clientWidgets: [{ key: 'active-services', order: 10, wide: true, Component: ActiveServicesWidget }],
  adminWidgets: [
    { key: 'failed-module-actions', order: 60, permission: 'admin.services.read', Component: FailedActionsWidget },
  ],
  adminClientTabs: [
    { key: 'services', label: 'Services', order: 10, permission: 'admin.services.read', Component: ClientServicesTab },
  ],
};
